import { useEffect, useMemo } from 'react';
import { Dropdown, Icon, Popup, Segment } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import './styles.scss';
import './managementSummary.scss';
import { get, isEmpty, map, size } from '../utils/lodash';
import { getGoalStatus, getCriticalStatus, getScaleStatus } from '../utils';
import { EmptyContainer } from '../components';
import { ViewMore } from '../containers/reports/reportHome';
import ActivityListTable, {
  hasLinkedActivities,
} from '../containers/activities/form/ActivityListTable';
import { isParentProgramActivity } from '../utils/activity';
import { ORGANISATION_REPORT_LOADING } from '../utils/constant';
import {
  getDepartmentHierarchy,
  getOrganizationReportData,
} from '../store/actions';

const REPORT_TYPE = 'management-summary';

// TAK-2081: only managers (Organisation Admins and Takso admins) can open an
// activity from the Summary; the server gives them read-only access to every
// activity in their organisation.
const CAN_OPEN_ROLES = ['admin', 'organisationAdmin'];

// Activity scope, as on the Activities menu, mapped to status ids
// (1 = Development, 2 = Active, 3 = Completed).
const SCOPE_STATUSES = {
  ongoing: [1, 2],
  completed: [3],
  all: [],
};

const scopeFromStatuses = (statuses = []) => {
  const key = [...statuses].map(Number).sort().join(',');
  const match = Object.keys(SCOPE_STATUSES).find(
    (scope) => SCOPE_STATUSES[scope].join(',') === key
  );
  return match || 'custom';
};

// Department tree → flat, indented options. Choosing a department includes
// everything beneath it (the Advanced filter does the same).
const flattenDepartments = (departments = [], level = 0, out = []) => {
  departments.forEach((dept) => {
    const descendantIds = [];
    const collect = (d) =>
      (d.children || []).forEach((child) => {
        descendantIds.push(child.id);
        collect(child);
      });
    collect(dept);
    out.push({
      id: dept.id,
      name: dept.name,
      level,
      ids: [dept.id, ...descendantIds],
    });
    flattenDepartments(dept.children || [], level + 1, out);
  });
  return out;
};

const sameIds = (a = [], b = []) =>
  a.length === b.length &&
  [...a].map(Number).sort().join(',') === [...b].map(Number).sort().join(',');

const sortOptions = (t) => [
  { key: 'date', text: <b>{t('common.date')}</b>, value: 'date' },
  {
    key: 'name',
    text: <b>{t('common.activity_name')}</b>,
    value: 'name',
  },
  { key: 'status', text: <b>{t('common.status')}</b>, value: 'status' },
];

// TAK-2081: each sort has its own order wording; the first is the default.
const SORT_ORDERS = {
  date: ['newest', 'oldest'],
  name: ['az', 'za'],
  status: ['least_developed', 'completed'],
};
const ordersFor = (sort) => SORT_ORDERS[sort] || SORT_ORDERS.date;
const orderOptions = (t, sort) =>
  ordersFor(sort).map((value) => ({
    key: value,
    text: <b>{t(`report.management_summary.${value}`)}</b>,
    value,
  }));

const defaultOrderFor = (sort) => ordersFor(sort)[0];

export const ManagementSummary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const managementSummaryReport = useSelector(
    (state) => state.report.managementSummaryReport
  );
  const reportState = useSelector((state) => state.report);
  const user = useSelector((state) => state.authentication.user);
  const deptHierarchy = useSelector(
    (state) => state.organization.deptHierarchy
  );

  const page = get(reportState, 'reportFilterOptions.page', 1);
  const statuses = get(reportState, 'reportFilterOptions.statuses', []);
  const departments = get(reportState, 'reportFilterOptions.departments', []);
  const msSort = get(reportState, 'reportFilterOptions.msSort') || 'date';
  const storedOrder = get(reportState, 'reportFilterOptions.msOrder');
  // an order that doesn't belong to the current sort falls back to its default
  const msOrder = ordersFor(msSort).includes(storedOrder)
    ? storedOrder
    : defaultOrderFor(msSort);
  const managerId = get(reportState, 'reportFilterOptions.managerId', null);

  // any Summary setting change reloads page 1; it's kept in the report filters
  const applyFilter = (change) =>
    dispatch(getOrganizationReportData(REPORT_TYPE, { ...change, page: 1 }));

  const managerOptions = [
    {
      key: 'all',
      value: 'all',
      text: t('report.management_summary.all_managers'),
    },
    ...get(managementSummaryReport, 'managers', []).map((manager) => ({
      key: manager.id,
      value: manager.id,
      text: manager.name,
    })),
  ];
  const loading = get(
    reportState,
    `managementSummaryLoading.${ORGANISATION_REPORT_LOADING.MANAGEMENT_SUMMARY_LOADING}`,
    false
  );

  useEffect(() => {
    if (isEmpty(deptHierarchy)) {
      dispatch(getDepartmentHierarchy());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const departmentList = useMemo(
    () => flattenDepartments(deptHierarchy || []),
    [deptHierarchy]
  );

  const departmentValue = isEmpty(departments)
    ? 'all'
    : get(
        departmentList.find((dept) => sameIds(dept.ids, departments)),
        'id',
        'custom'
      );

  const departmentOptions = [
    {
      key: 'all',
      value: 'all',
      text: t('report.management_summary.all_departments'),
    },
    ...departmentList.map((dept) => ({
      key: dept.id,
      value: dept.id,
      text: dept.name,
      content: (
        <span
          className={`ms-filters__dept ms-filters__dept--level-${Math.min(
            dept.level,
            4
          )}`}
        >
          {dept.name}
        </span>
      ),
    })),
    ...(departmentValue === 'custom'
      ? [
          {
            key: 'custom',
            value: 'custom',
            text: t('report.management_summary.custom_selection'),
            disabled: true,
          },
        ]
      : []),
  ];

  const scopeValue = scopeFromStatuses(statuses);
  const scopeOptions = [
    {
      key: 'ongoing',
      value: 'ongoing',
      text: t('activity_status.ongoing_activities'),
    },
    {
      key: 'completed',
      value: 'completed',
      text: t('activity_status.completed_activities'),
    },
    { key: 'all', value: 'all', text: t('activity_status.all_activities') },
    ...(scopeValue === 'custom'
      ? [
          {
            key: 'custom',
            value: 'custom',
            text: t('report.management_summary.custom_selection'),
            disabled: true,
          },
        ]
      : []),
  ];

  const onDepartmentChange = (value) => {
    const chosen = departmentList.find((dept) => dept.id === value);
    applyFilter({ departments: chosen ? chosen.ids : [] });
  };

  const onScopeChange = (value) => {
    applyFilter({ statuses: SCOPE_STATUSES[value] || [] });
  };

  const canOpen = CAN_OPEN_ROLES.includes(get(user, 'role'));
  const getRowHref = (item) => (canOpen ? `/activities/${item.id}` : null);

  const development = get(managementSummaryReport, 'development', 0);
  const active = get(managementSummaryReport, 'active', 0);
  const completed = get(managementSummaryReport, 'completed', 0);
  const counts = [
    {
      key: 'development',
      label: t('activity_status.in_development'),
      value: development,
      dot: 'warning',
    },
    {
      key: 'active',
      label: t('activity_status.active'),
      value: active,
      dot: 'success',
    },
    {
      key: 'completed',
      label: t('activity_status.completed'),
      value: completed,
      dot: 'completed',
    },
    {
      key: 'total',
      label: t('report.management_summary.total'),
      value: Number(development) + Number(active) + Number(completed),
      dot: null,
    },
  ];

  // One result line per rating: "Post : 7.69  Pre : 6.19"
  const resultLine = (rating) =>
    map(rating, (value, key) =>
      key !== 'outcomeId' ? `${getScaleStatus(key)} ${value}` : ''
    )
      .join(' ')
      .trim();

  // Completed activities show each objective's ratings; others their
  // progress (%) and any to-do warning.
  const showsRatings = (data) =>
    ((!data.isLinkedActivity && get(data, 'status.type') === 'COMPLETED') ||
      (data.isLinkedActivity && data.childApproved)) &&
    !isParentProgramActivity(data.activityPlanTypeId);

  const renderProgress = (data) => {
    const note = get(data, 'status.note');
    return (
      <div className="percentage">
        <div>{`${get(data, 'status.percentage', 0)}%`}</div>
        {note ? (
          <div className="per-right">
            <Popup
              content={note.label || null}
              trigger={
                <Icon
                  name="warning circle"
                  color={getCriticalStatus(note.criticality)}
                  className="icon-org-summary"
                />
              }
            />
          </div>
        ) : null}
      </div>
    );
  };

  // TAK-2081: each objective with its own result on the same line. Ratings
  // are matched to objectives by outcome (falling back to their order when
  // the server sends no outcome ids).
  const renderObjectivesAndResults = (data) => {
    const objectives = !isEmpty(data.objectives)
      ? data.objectives
      : map(data.outcomes, (title) => ({ title }));
    const ratings = get(data, 'scale', []);
    const withRatings = showsRatings(data);
    const ratingFor = (objective, index) => {
      if (objective.outcomeId != null) {
        return ratings.filter(
          (rating) => Number(rating.outcomeId) === Number(objective.outcomeId)
        );
      }
      return ratings[index] ? [ratings[index]] : [];
    };

    if (isEmpty(objectives)) {
      return (
        <div className="ms-objres__row">
          <div>-</div>
          <div>{withRatings ? '-' : renderProgress(data)}</div>
        </div>
      );
    }

    const rows = objectives.map((objective, index) => (
      <div className="ms-objres__row" key={`${data.id}_obj_${index}`}>
        <div className="ms-objres__objective">{objective.title}</div>
        <div className="ms-objres__result">
          {withRatings
            ? ratingFor(objective, index).map((rating, r) => (
                <div key={r}>{resultLine(rating) || '-'}</div>
              ))
            : index === 0
              ? renderProgress(data)
              : null}
        </div>
      </div>
    ));
    if (size(rows) > 2) {
      return <ViewMore minCount={2} dataSet={rows} t={t} />;
    }
    return <div>{rows}</div>;
  };

  // TAK-2081: what a program shows under its name. Before green light:
  // "No dates" until its dates are set, then "Green light <date>". After
  // green light: its dates. Then "No activities" or "<n> activities".
  const programLines = (data) => {
    const hasDates = data.dates && data.dates !== '-';
    const preGreenLight = Number(data.statusId) === 1;
    let when = t('report.management_summary.no_dates');
    if (preGreenLight && data.greenLightDate && hasDates) {
      when = t('report.management_summary.green_light_on', {
        date: data.greenLightDate,
      });
    } else if (hasDates) {
      when = data.dates;
    }
    const count = hasLinkedActivities(data) ? data.linkedActivities.length : 0;
    const activities =
      count > 0
        ? t('activity.home.program_list.linked_count', { count })
        : t('report.management_summary.no_activities');
    return [when, activities];
  };

  const columns = [
    {
      width: 5,
      title: t('common.activity_name'),
      render: (data, ctx) => {
        // a linked activity whose program isn't listed above it
        const partOf =
          ctx.level === 0 &&
          data.isLinkedActivity &&
          get(data, 'parentProjectData.name', '-') !== '-'
            ? data.parentProjectData.name
            : null;
        return (
          <div className="ms-table__name">
            <div
              className={`ms-dot ms-dot--${getGoalStatus(
                get(data, 'status.type')
              )}`}
            />
            <div>
              <div className="ms-table__title">{data.activityName}</div>
              {isParentProgramActivity(data.activityPlanTypeId) ? (
                programLines(data).map((line, i) => (
                  <div className="ms-table__meta" key={i}>
                    {line}
                  </div>
                ))
              ) : data.dates !== '-' ? (
                <div className="ms-table__meta">{data.dates}</div>
              ) : null}
              {partOf ? (
                <div className="ms-table__meta">
                  {t('activity.home.program_list.part_of')} <b>{partOf}</b>
                </div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      width: 2,
      title: t('common.type'),
      render: (data) => data.activityType,
    },
    {
      width: 2,
      title: t('report.management_summary.contact'),
      // TAK-2081: a guest manager (outside the organisation) shows as Guest;
      // one from a different department than the program shows it
      render: (data) => {
        const owner = get(data, 'owner', {});
        if (owner.isGuest) {
          return t('report.management_summary.guest');
        }
        return (
          <div>
            <div>{owner.name || '-'}</div>
            {owner.showDepartment ? (
              <div className="ms-table__meta">{owner.departmentName}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      width: 7,
      title: (
        <div className="ms-objres__row ms-objres__row--header">
          <div>{t('common.objectives')}</div>
          <div>{t('report.management_summary.status_result')}</div>
        </div>
      ),
      render: (data) => renderObjectivesAndResults(data),
    },
  ];

  return (
    <div className="ms">
      <Segment raised className="ms-overview">
        <div className="ms-overview__bar">
          <div className="ms-filters">
            {!isEmpty(departmentList) ? (
              <label className="ms-filters__field">
                <span className="ms-filters__label">
                  {t('common.department')}
                </span>
                <Dropdown
                  selection
                  className="ms-filters__dropdown"
                  value={departmentValue}
                  options={departmentOptions}
                  onChange={(e, { value }) => onDepartmentChange(value)}
                />
              </label>
            ) : null}
            <label className="ms-filters__field">
              <span className="ms-filters__label">
                {t('common.activities')}
              </span>
              <Dropdown
                selection
                className="ms-filters__dropdown"
                value={scopeValue}
                options={scopeOptions}
                onChange={(e, { value }) => onScopeChange(value)}
              />
            </label>
            <label className="ms-filters__field">
              <span className="ms-filters__label">
                {t('report.management_summary.managed_by')}
              </span>
              <Dropdown
                selection
                search
                className="ms-filters__dropdown"
                value={managerId || 'all'}
                options={managerOptions}
                onChange={(e, { value }) =>
                  applyFilter({ managerId: value === 'all' ? null : value })
                }
              />
            </label>
          </div>

          <div className="ms-counts">
            {counts.map((count) => (
              <div
                key={count.key}
                title={count.label}
                className={`ms-counts__item${
                  count.dot ? '' : ' ms-counts__item--total'
                }`}
              >
                {count.dot ? (
                  <span className={`ms-dot ms-dot--${count.dot}`} />
                ) : null}
                {count.dot ? null : (
                  <span className="ms-counts__label">{count.label}</span>
                )}
                <span className="ms-counts__value">{count.value}</span>
              </div>
            ))}
          </div>
        </div>
      </Segment>

      <Segment raised loading={loading} className="management-summary ms-list">
        <div className="ms-list__sort">
          {t('common.sort_by')}: &nbsp;
          <Dropdown
            inline
            selectOnBlur={false}
            value={msSort}
            options={sortOptions(t)}
            onChange={(e, { value }) =>
              applyFilter({ msSort: value, msOrder: defaultOrderFor(value) })
            }
          />
          <span className="ms-list__sort-gap" />
          {t('common.order_by')}: &nbsp;
          <Dropdown
            inline
            selectOnBlur={false}
            value={msOrder}
            options={orderOptions(t, msSort)}
            onChange={(e, { value }) => applyFilter({ msOrder: value })}
          />
        </div>

        {!isEmpty(get(managementSummaryReport, 'projects', [])) ? (
          <ActivityListTable
            columns={columns}
            data={managementSummaryReport.projects}
            getRowHref={getRowHref}
            openInNewTab
            linkedMode="all"
            getRowClassName={(item) =>
              isParentProgramActivity(item.activityPlanTypeId)
                ? 'ms-row--program'
                : ''
            }
            currentUserId={get(user, 'id')}
            page={page}
            noOfPages={managementSummaryReport.pages}
            onPageChange={(nextPage) =>
              dispatch(
                getOrganizationReportData(REPORT_TYPE, { page: nextPage })
              )
            }
          />
        ) : (
          <EmptyContainer msg={t('report.no_data_available')} />
        )}
      </Segment>
    </div>
  );
};
